import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { updateIPName, IPConfig } from '../../utils/ipConfig';

interface IPNameModalProps {
  ip: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: IPConfig) => void;
  initialName?: string;
  initialDescription?: string;
  initialColor?: string;
}

const IPNameModal: React.FC<IPNameModalProps> = ({
  ip,
  isOpen,
  onClose,
  onSave,
  initialName = '',
  initialDescription = '',
  initialColor = ''
}) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [color, setColor] = useState(initialColor);

  useEffect(() => {
    setName(initialName);
    setDescription(initialDescription);
    setColor(initialColor || '#6366F1'); // Couleur par défaut
  }, [initialName, initialDescription, initialColor, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const updatedConfig = updateIPName(ip, name, description, color);
    onSave(updatedConfig);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
            Nommer l'adresse IP
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          <div className="mb-4">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Adresse IP: <span className="font-semibold text-gray-800 dark:text-white">{ip}</span>
            </div>
            
            <label htmlFor="ipName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nom de l'adresse IP
            </label>
            <input
              type="text"
              id="ipName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-field w-full"
              placeholder="ex: Serveur principal"
              required
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="ipDescription" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description (optionnelle)
            </label>
            <textarea
              id="ipDescription"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input-field w-full"
              placeholder="ex: Serveur OVH dédié pour les sites principaux"
              rows={3}
            />
          </div>
          
          <div className="mb-6">
            <label htmlFor="ipColor" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Couleur (optionnelle)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                id="ipColor"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="h-10 w-10 rounded-md cursor-pointer border-0"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {color}
              </span>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md shadow-sm"
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IPNameModal; 